import { Component, inject, OnInit } from '@angular/core'
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { InputTextModule } from "primeng/inputtext";
import { PaginatorModule, PaginatorState } from "primeng/paginator";
import { TableModule } from 'primeng/table'
import { IconFieldModule } from 'primeng/iconfield'
import { InputIconModule } from 'primeng/inputicon'
import { Router } from '@angular/router'
import { Group } from '../../../../models/group.model'
import { LdapManagerService } from '../../../../core/services/ldap-manager.service'
import { LoggerService } from '@danangelus/ngx-app-core'
import { ButtonModule } from 'primeng/button'
import { TooltipModule } from 'primeng/tooltip'
import { debounceTime, distinctUntilChanged, Subject, switchMap, tap } from 'rxjs';

const log = LoggerService.get('ListGroupComponent')

/**
 * Component to handle the display of user groups
 *
 * @author Connor Wheatley
 * @author Dan Bennett
 */
@Component({
    selector: 'list-group',
    standalone: true,
    imports: [
        // primeng
        IconFieldModule,
        InputIconModule,
        TableModule,
        ButtonModule,
        TooltipModule,
        InputTextModule,
        InputGroupModule,
        InputGroupAddonModule,
        PaginatorModule,
    ],
    templateUrl: './list-group.component.html',
    styleUrl: './list-group.component.sass'
})
export class ListGroupComponent implements OnInit {

    // services
    router = inject(Router)
    ldapManagerService = inject(LdapManagerService)

    // data needed for sorting and pagination
    loading = true
    sortColumn = 'name'
    sortedAsc = true
    pageNumber = 1
    pageSize = 25
    totalRecords = 0
    firstRecord = 1

    // data needed for searching groups in search bar
    query = ''
    searchSubject = new Subject<string>()

    groups: Group[] = []

    async ngOnInit(): Promise<void> {
        this.searchSubject.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap(query => {
                this.query = query.length > 2 ? query : ''
                if (query.length <= 2) {
                    this.pageNumber = 1 // Reset to the first page when clearing the query
                }
            }),
            switchMap(async () => this.groups = await this.getGroups())
        ).subscribe()
        this.searchSubject.next(this.query)
    }

    /**
     * Search for users using query more than 3 characters long. If the query box is cleared the search is reset.
     */
    async getGroups(sortColumn = `${this.sortColumn}-${this.sortedAsc ? 'asc' : 'desc'}`): Promise<Group[]> {
        log.debug('getGroups', `Finding groups ${this.query}`)
        this.loading = true
        const page = await this.ldapManagerService.findGroups(this.query, this.pageNumber, this.pageSize, sortColumn)
        this.totalRecords = page?.totalElements || 0
        this.loading = false
        return page?.content.map((group: any) => new Group(group)) || []
    }

    async changePage(event: PaginatorState) {
        log.debug('getGroups', `Finding groups ${this.query}`)
        this.pageNumber = event.page! + 1
        this.pageSize = event.rows!
        this.groups = await this.getGroups()
    }

    async sortBy(column: any): Promise<void> {
        if (this.sortColumn === column.field) {
            this.sortedAsc = !this.sortedAsc
        }
        const sortColumn = `${column.field}-${this.sortedAsc ? 'asc' : 'desc'}`
        this.sortColumn = column.field
        const groups = await this.getGroups(sortColumn)
        // Did this to prevent page loop when re-applying the group list (seems to be a bug in PrimeNG)
        this.groups.splice(0, this.groups.length)
        this.groups.push(...groups)
        log.debug('sortBy', `Sorted by ${sortColumn}`)
    }

    async search(event: Event) {
        log.debug('search', 'Searching for groups')
        const queryStr = (event.target as HTMLInputElement).value

        if (queryStr === '' && this.query !== '') {
            // Explicitly trigger search for cleared input
            this.searchSubject.next('');
        } else {
            this.searchSubject.next(queryStr);
        }
    }

    async createGroup(): Promise<void> {
        log.debug('createGroups', 'Creating a new group')
        await this.router.navigateByUrl('/administration/group/create')
    }

    async editGroup(id: string): Promise<void> {
        log.debug('editGroup', 'Editing a group')
        await this.router.navigateByUrl(`/administration/group/${id}`)
    }
}
